/**
 * Wasiyat cryptography chassis.
 *
 * v0 stack:
 *  - PBKDF2-SHA512 @ 1_000_000 iterations + per-vault random 32-byte salt
 *  - AES-GCM-256 for vault payload at rest
 *  - WebAuthn PublicKeyCredential for biometric unlock layer (passkey)
 *  - SHA-256 for phone/email hashing (not for password storage)
 *
 * v0.2 (TODO): swap to Argon2id via argon2-browser WASM. Interface unchanged.
 * The PBKDF2-SHA512 baseline at 1M iters is well above OWASP 2023 minimums
 * (210k SHA-512) and gives v0 a defensible security posture without WASM deps.
 *
 * Nothing in this module talks to the network.
 */

const KDF_ITERS = 1_000_000;
const KDF_HASH = 'SHA-512';
const SALT_BYTES = 32;
const IV_BYTES = 12;
const AAD_VERSION = new TextEncoder().encode('wasiyat-v1');

export interface VaultEnvelope {
  version: 1;
  kdf: 'pbkdf2-sha512';
  iters: number;
  salt: string; // base64
  iv: string; // base64
  ciphertext: string; // base64
  webauthn_handle: string | null; // credentialId (base64url) if passkey bound
}

function b64encode(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function b64decode(s: string): Uint8Array {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}

function randomBytes(n: number): Uint8Array {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return a;
}

async function deriveKey(passcode: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passcode),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: KDF_ITERS, hash: KDF_HASH },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptVault(passcode: string, payload: unknown, webauthnHandle: string | null): Promise<VaultEnvelope> {
  const salt = randomBytes(SALT_BYTES);
  const iv = randomBytes(IV_BYTES);
  const key = await deriveKey(passcode, salt);
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: AAD_VERSION }, key, plaintext));
  return {
    version: 1,
    kdf: 'pbkdf2-sha512',
    iters: KDF_ITERS,
    salt: b64encode(salt),
    iv: b64encode(iv),
    ciphertext: b64encode(ct),
    webauthn_handle: webauthnHandle,
  };
}

export async function decryptVault<T>(passcode: string, env: VaultEnvelope): Promise<T> {
  if (env.version !== 1) throw new Error(`unknown vault version ${env.version}`);
  if (env.kdf !== 'pbkdf2-sha512') throw new Error(`unknown kdf ${env.kdf}`);
  const salt = b64decode(env.salt);
  const iv = b64decode(env.iv);
  const ct = b64decode(env.ciphertext);
  const key = await deriveKey(passcode, salt);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv, additionalData: AAD_VERSION }, key, ct);
  return JSON.parse(new TextDecoder().decode(pt)) as T;
}

export async function sha256(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return b64encode(new Uint8Array(buf));
}

// ──────────────────── WebAuthn (passkey) ─────────────────
// Used as a SECOND layer on top of passcode. Passcode still required for
// vault decryption; passkey gates UI access (faster than typing).

export interface PasskeyHandle {
  credentialId: string; // base64url
  publicKeyAlgorithm: number;
  createdAt: string;
}

export async function registerPasskey(displayName: string): Promise<PasskeyHandle | null> {
  if (!('credentials' in navigator) || !('PublicKeyCredential' in window)) return null;
  const challenge = randomBytes(32);
  const userId = randomBytes(16);
  try {
    const cred = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: 'Wasiyat', id: location.hostname },
        user: { id: userId, name: displayName, displayName },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 }, // ES256
          { type: 'public-key', alg: -257 }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          residentKey: 'preferred',
          userVerification: 'required',
        },
        attestation: 'none',
        timeout: 60_000,
      },
    });
    if (!cred || cred.type !== 'public-key') return null;
    const pkc = cred as PublicKeyCredential;
    const rawId = new Uint8Array(pkc.rawId);
    const response = pkc.response as AuthenticatorAttestationResponse;
    return {
      credentialId: b64encode(rawId),
      publicKeyAlgorithm: response.getPublicKeyAlgorithm?.() ?? -7,
      createdAt: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export async function verifyPasskey(handle: PasskeyHandle): Promise<boolean> {
  if (!('credentials' in navigator)) return false;
  try {
    const challenge = randomBytes(32);
    const credId = b64decode(handle.credentialId);
    const result = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [{ id: credId, type: 'public-key' }],
        userVerification: 'required',
        timeout: 60_000,
      },
    });
    return result !== null && result.type === 'public-key';
  } catch {
    return false;
  }
}

// ──────────────────── per-share envelope ────────────────
// Encrypt a Shamir share with a nominee-specific hint (NOT public key — the
// share is meaningless without K-of-N other shares, so this is defense in
// depth, not the primary boundary).

export async function wrapShare(share: Uint8Array, hint: string): Promise<string> {
  const salt = randomBytes(16);
  const iv = randomBytes(IV_BYTES);
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(`wasiyat-share:${hint}`),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100_000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, share));
  const merged = new Uint8Array(salt.length + iv.length + ct.length);
  merged.set(salt, 0);
  merged.set(iv, salt.length);
  merged.set(ct, salt.length + iv.length);
  return b64encode(merged);
}

export async function unwrapShare(wrapped: string, hint: string): Promise<Uint8Array> {
  const merged = b64decode(wrapped);
  const salt = merged.slice(0, 16);
  const iv = merged.slice(16, 16 + IV_BYTES);
  const ct = merged.slice(16 + IV_BYTES);
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(`wasiyat-share:${hint}`),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100_000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );
  return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct));
}

export const cryptoMeta = {
  kdf: 'pbkdf2-sha512',
  iters: KDF_ITERS,
  envelope: 'AES-GCM-256 + AAD',
  passkey: 'WebAuthn platform-bound',
  todo_v02: 'Argon2id WASM',
};
